import React, { useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { PageLoading, SidebarHeader } from '@pv/common/components';

import { VenueDataImportsList } from './VenueDataImportsList';
import { VenueDataImportForm } from './VenueDataImportForm';
import { TripleseatImportForm } from './TripleseatImportForm';
import { DriveImportForm } from './DriveImportForm';
import { Button, Stack } from '@mui/material';

interface DataImportsPanelProps {
  venueId: string;
}

interface VenueDataImport {
  id: string;
  createdAt: string;
  updatedAt: string;
  status: string;
  errorMessages: string[];
  stagingResults?: any;
  storageUrl?: string | null;
}

const venueDataImportsQuery = gql`
  query VenueDataImportsQuery($venueId: ID!) {
    venueDataImports(venueId: $venueId) {
      id
      createdAt
      updatedAt
      status
      errorMessages
      stagingResults
      storageUrl
    }
  }
`;

export const DataImportsPanel = ({ venueId }: DataImportsPanelProps) => {
  const [showSpreadsheetForm, setShouldShowSpreadsheetForm] = useState(false);
  const [showTripleseatForm, setShouldShowTripleseatForm] = useState(false);
  const [showDriveForm, setShowDriveForm] = useState(false);
  const [venueDataImports, setVenueDataImports] = React.useState<
    VenueDataImport[]
  >([]);

  const { loading, error } = useQuery(venueDataImportsQuery, {
    variables: { venueId: venueId },
    onCompleted: (data) => {
      setVenueDataImports(data.venueDataImports);
    },
  });

  if (loading) {
    return <PageLoading />;
  }

  if (error) {
    return <div>{error.message}</div>;
  }

  return (
    <div>
      <SidebarHeader title="Venue Data Imports" />
      <VenueDataImportsList
        venueDataImports={venueDataImports}
        autoOpenImportId={new URLSearchParams(window.location.search).get('import_id') ?? undefined}

      />

      <Stack direction="row" spacing={2} padding={2}>
        {!showSpreadsheetForm && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setShouldShowSpreadsheetForm(true)}
            sx={{ margin: '10px 0px' }}
          >
            Create New Spreadsheet Import
          </Button>
        )}

        {!showTripleseatForm && (
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setShouldShowTripleseatForm(true)}
          >
            Create New Tripleseat Import
          </Button>
        )}

        <Button
          variant="outlined"
          color="secondary"
          onClick={() => setShowDriveForm(true)}
          sx={{ margin: '10px 0px' }}
        >
          Import from Cloud Storage
        </Button>
      </Stack>

      <VenueDataImportForm
        show={showSpreadsheetForm}
        onClose={() => setShouldShowSpreadsheetForm(false)}
        venueId={venueId}
        onCompleted={(dataImport) => {
          setVenueDataImports([...venueDataImports, dataImport]);
        }}
      />

      <TripleseatImportForm
        show={showTripleseatForm}
        onClose={() => setShouldShowTripleseatForm(false)}
        venueId={venueId}
        onCompleted={(dataImport) => {
          setVenueDataImports([...venueDataImports, dataImport]);
        }}
      />

      <DriveImportForm
        show={showDriveForm}
        onClose={() => setShowDriveForm(false)}
        venueId={venueId}
        onCompleted={(dataImport) => {
          setVenueDataImports([...venueDataImports, dataImport]);
        }}
      />
    </div>
  );
};
